import {Component, Input, isDevMode, OnInit} from '@angular/core';
import { getTenantLetterAssets } from '../../../../_shared/utils/letter-assets';
import {
  AlertService,
  ConfirmDialogService,
  ContactsStoreService, CrmStoreService, FileStoreService,
  IAlertType,
  PersonsStoreService,
  ProjectsStoreService, StaffStoreService
} from '../../../../_shared/services';
import {
  CommentFeatureCardComponent
} from "../../../../core/comment/comment-feature-card/comment-feature-card.component";
import {
  CommentProjectCardComponent
} from "../../../../core/comment/comment-project-card/comment-project-card.component";
import { ClrIconModule, ClrModalModule } from "@clr/angular";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { Projects, Personen, Positions, Files, SingleCompany} from '../../../../_shared/interfaces';
import { Router, RouterLink } from '@angular/router';
import moment from "moment";
import { formatCurrency, NgForOf, NgIf } from "@angular/common";
import { getTenantStorage } from '../../../../_shared/utils/tenant-context';

import { environment as env } from "../../../../../environments/environment";
import { filesBaseUrl } from "../../../../_shared/utils/rest-url";
import { ClarityIcons, fileIcon, noteIcon } from "@cds/core/icon";
import PackageJson from "../../../../../../package.json";

@Component({
  selector: 'app-project-details-stammdaten',
  standalone: true,
  imports: [
    NgIf,
    ClrIconModule,
    RouterLink,
    NgForOf,
    ClrModalModule,
    NgxExtendedPdfViewerModule,
    CommentFeatureCardComponent,
    CommentProjectCardComponent
  ],
  templateUrl: './project-details-stammdaten.component.html',
  styleUrl: './project-details-stammdaten.component.sass'
})
export class ProjectDetailsStammdatenComponent implements OnInit {
  positions: Positions[] = [];
  project: Projects | null = null;
  company: SingleCompany | null = null;
  person: Personen | null = null;
  project_id: string = '';
  staffFirstName: string = '';
  staffLastName: string = '';
  requestFiles: Files[] = [];
  public showPDF = false;
  modalContent: string = '';
  modalUrl: string = '';

  public ppsVersion: string = PackageJson.version;

  private filesDirectory = filesBaseUrl();

  @Input() cidstd: number = 0;
  @Input() pridstd: string = '';
  @Input() basedata: Projects | null = null;

  // Für Ausdruck
  basic = false;
  clrModalSize: string = '';

  documentDefinition = this.buildMemoDocumentDefinition([], [[]]);

  constructor(
      private css: ContactsStoreService,
      private stss: StaffStoreService,
      private pss: PersonsStoreService,
      private projss: ProjectsStoreService,
      private fss: FileStoreService,
      private crmss: CrmStoreService,
      private confirmDialog: ConfirmDialogService,
      private router: Router,
      private alertDispatcher: AlertService,
  ) {
    ClarityIcons.addIcons(fileIcon, noteIcon)
  }

  ngOnInit() {
    this.loadCompanyData(this.cidstd);
    // Den Ansprechpartner NICHT hier laden: er haengt am Projekt
    // (employee_leader), nicht an der Firma. loadProject() setzt ihn, sobald
    // das Projekt da ist.
    this.loadProject(this.cidstd, this.pridstd);
    this.loadProjectsPositions(this.cidstd + '.' + this.pridstd);  // Poisitionsliste fürs Memo
    if (this.basedata?.employee_phm) this.staffStatus(this.basedata.employee_phm.toString());
    this.project_id = this.cidstd + '.' + this.pridstd;
    this.clrModalSize = 'xl';
    this.getFilesSet([99], this.project_id, 0);
  }

  ngOnChanges() {
    this.loadProject(this.cidstd, this.pridstd
    );
  }

  loadCompanyData(company: number) {
    this.css.getSingleCompany(company).subscribe({
      next: res => {
        if (!res || !res.object || res.object.length === 0) {
          this.alertDispatcher.alertConfig.message = 'Keine Firmendaten gefunden!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
          return;
        }
        this.company = res.object[0];
      },
      error: () => {
        return;
      }
    })
  }

  loadPersonData(person: number) {
    return this.pss.getSinglePersons(person).subscribe({
      next: res => {
        if (!res || !res.object || res.object.length === 0) {
          this.alertDispatcher.alertConfig.message = 'Keine Personendaten gefunden!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
          return;
        }
        this.person = res.object[0];
      },
      error: () => {
        return;
      }
    })
  }

  /// Der Ansprechpartner des Projekts ist `employee_leader`. Vorher wurde
  /// stattdessen die erste Person der Kundenfirma angezeigt -- bei Firma 756 also
  /// Bokarius statt des tatsaechlich hinterlegten Goltz. Auf die Firmenliste
  /// faellt die Anzeige nur noch zurueck, wenn am Projekt niemand hinterlegt ist.
  private resolveContactPerson(project: Projects | null) {
    const leader = project?.employee_leader;
    if (leader) {
      this.loadPersonData(leader);
    } else {
      this.loadCompanyPerson(this.cidstd);
    }
  }

  loadCompanyPerson(companyId: number) {
    this.pss.getPersonInCompany(companyId, true).subscribe({
      next: res => {
        if (!res || !res.object || res.object.length === 0) {
          this.person = null;
          return;
        }
        this.person = res.object[0];
      },
      error: () => {
        this.person = null;
      }
    });
  }

  loadProject(company: number, project: string) {
    // 'all' statt '0': get_projects behandelt nur '' und 'all' als "kein Filter",
    // alles andere wird als Status gefiltert. Mit '0' kam nie ein Projekt zurueck
    // -- kein Status heisst so. `this.project` blieb dadurch immer null.
    this.projss.filterProjectsPPS(company, project, 0, 'all').subscribe({
      next: data1 => {
        if (!data1 || !data1.object || data1.object.length === 0) {
          this.alertDispatcher.alertConfig.message = 'Kein Projekt gefunden!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
          return;
        }
        this.project = data1.object[0];
        this.resolveContactPerson(this.project);
      },
      complete: () => { },
      error: () => {
        return
      }
    });
  };

  private getFilesSet(doctype: number[], projectID: string, jobID: number) {
    this.fss.getFilesSet(doctype, projectID, jobID, 0).subscribe({
      next: data => {
        if (!data || !data.object || data.object.length === 0) {
          this.alertDispatcher.alertConfig.message = 'Keine Dateien gefunden!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
          return;
        }
        this.requestFiles = data.object;
      },
      complete: () => {},
      error: () => {
        return
      }
    })
  }

  deleteProject() {
    this.projss.getDeleteProjectImpact(this.cidstd, this.pridstd).subscribe({
      next: impactResponse => {
        const impact = impactResponse?.object?.[0];
        if (!this.confirmDialog.confirmProjectDelete(this.cidstd, this.pridstd, impact)) {
          return;
        }

        this.projss.deleteProject(this.cidstd, this.pridstd)
            .subscribe({
              next: () => {
                if (this.project?.cosmos_id) { this.crmss.crmDeleteProject(this.cidstd, parseInt(this.project?.cosmos_id)) }
              },
              complete: () =>
                  this.router.navigate(['/projects/overview'])
            });
      },
      error: () => {
        if (this.confirmDialog.confirmProjectDelete(this.cidstd, this.pridstd)) {
          this.projss.deleteProject(this.cidstd, this.pridstd)
              .subscribe({
                next: () => {
                  if (this.project?.cosmos_id) { this.crmss.crmDeleteProject(this.cidstd, parseInt(this.project?.cosmos_id)) }
                },
                complete: () =>
                    this.router.navigate(['/projects/overview'])
              });
        }
      }
    });
  }

  // Load positions for memo
  loadProjectsPositions(prid: string) {
    this.projss.getPositions(prid, 0, 0).subscribe({
      next: data => {
        if (!data || !data.object || data.object.length === 0) {
          this.alertDispatcher.alertConfig.message = 'Keine Projektpositionen gefunden!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
          return;
        }
        this.positions = data.object;
      },
      complete: () => {},
      error: () => {
        return
      },
    });
  }

  staffStatus(staffID: string){
    this.stss.getStaffById(parseInt(staffID))
        .subscribe({
          next: staff => {
            if (!staff || !staff.object || staff.object.length === 0) {
              this.alertDispatcher.alertConfig.message = 'Keinen Mitarbeiter gefunden!';
              this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
              return;
            }
            this.staffFirstName = staff.object[0].firstname;
            this.staffLastName = staff.object[0].lastname;
          },
          error: () => {
            return
          }
        });
  }


  createMemo(positions: any[]) {
    let projTable = [[{ text: 'Projekt' }, { text: this.cidstd + '.' + this.pridstd }]];
    let posTable = [[{ text: 'Pos.'}, { text: 'Bezeichnung' }, { text: 'Zeichnung' }, { text: 'Material'}, { text: 'Seriennummer'}, { text: 'Anzahl', alignment: 'right'}]];

    // create head table
    projTable.push([ { text: 'Bezeichnung '}, { text: this.basedata?.name_project ?? ''} ]);
    projTable.push([ { text: 'Laufzeit '}, { text: this.formatMemoDate(this.basedata?.start_project) + ' - ' + this.formatMemoDate(this.basedata?.end_project) } ]);
    projTable.push([ { text: 'Kunde '}, { text: this.basedata?.company_name.toString() ?? '' } ]);
    if (this.person) { projTable.push([ { text: 'Ansprechpartner '}, { text: this.person.salutation + ' ' + this.person.firstname + ' ' + this.person.lastname } ]); }
    projTable.push([ { text: 'Projektummer des Kunden '}, { text: this.basedata?.request_id ?? ''} ]);
    if (this.basedata?.order_id) projTable.push([ { text: 'Bestellnummer des Kunden '}, { text: this.basedata?.order_id ?? '' } ]);
    if (this.basedata?.cosmos_id) projTable.push([ { text: 'COSMOS-ID '}, { text: this.basedata?.cosmos_id ?? '' } ]);
    if ((this.basedata?.budget ?? 0) > 0) projTable.push([ { text: 'VK netto '}, { text: formatCurrency(this.basedata?.budget ?? 0, 'de', 'EUR', 'EUR')} ]);


    positions.forEach(position => {
      if (!position.technology) {
        switch(position.positions_part) {
          case 0: {
            posTable.push(
                [ { text: position.position.toString() }, { text: position.name }, { text: position.technical_drawing }, { text: position.material ?? '' }, { text: position.serial_nr }, { text: position.number_of_pieces?.toString() ?? '', alignment: 'right' }]
            );
            break;
          }
          default: {
            posTable.push(
                [ { text: position.position + '.' + position.positions_part, style: 'positionText' } as any, { text: position.name }, { text: position.technical_drawing }, { text: position.material ?? '' },  { text: position.serial_nr }, { text: position.number_of_pieces?.toString() ?? '', alignment: 'right' }]
            );
            break;
          }
        }
      }
    });

    this.documentDefinition = this.buildMemoDocumentDefinition(projTable, posTable);

    this.generatePdf();
  }

  private formatMemoDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const parsed = moment(value, ['DD.MM.YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value;
  }

  private buildMemoDocumentDefinition(projTable: any[], posTable: any[]) {
    const baseDefinition: any = getTenantLetterAssets().a4Letter2nd;

    return {
      ...baseDefinition,
      info: {
        ...(baseDefinition.info ?? {}),
        title: 'PPS Project Memo',
        subject: 'MEMO ' + this.cidstd + '.' + this.pridstd,
        revison: this.ppsVersion,
      },
      content: [
        {
          columns: [
            {
              layout: 'lightHorizontalLines',
              table: {
                headerRows: 0,
                widths: ['auto', 'auto'],
                body: projTable,
              },
              style: 'address',
              margin: [30, 30]
            },
            {
              width: 1,
              text: ' '
            },
            {
              width: 150,
              text: '',
              style: 'address',
              margin: [0, 30]
            }
          ],
          columnGap: 10
        },
        {
          layout: 'lightHorizontalLines',
          table: {
            headerRows: 1,
            widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: posTable,
          },
          style: 'table',
          margin: [30, 30]
        }
      ]
    };
  }

  generatePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);

    documentGenerator.getDataUrl((dataUrl: string) => {
      const targetElement = document.querySelector('#documentContainer');
      targetElement!.innerHTML = '';

      if (!targetElement) {
        this.alertDispatcher.alertConfig.message = 'PDF-Container nicht gefunden!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
        return; // Exit the function if the element doesn't exist
      }

      const pdfIframe = document.createElement('iframe');
      pdfIframe.src = dataUrl;
      pdfIframe.width = '100%';
      pdfIframe.height = '100%';
      targetElement.appendChild(pdfIframe);
    })
  }

  async downloadPdf(){
    if (confirm('Wollen Sie das memo zum Projekt ' + 'Memo_' + this.cidstd + '.' + this.pridstd + '.pdf wirklich herunterladen?')) {
      const documentGenerator = pdfMake.createPdf(this.documentDefinition as any).download('Memo' + this.cidstd + '.' + this.pridstd + '.pdf');
    }

    await this.safePdf();

    this.alertDispatcher.alertConfig.message = 'Datei ' + 'Memo_' + this.cidstd + '.' + this.pridstd + '.pdf wurde herunterladen.';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
  }

  async printPdf() {
    if (confirm('Wollen Sie die Datei ' + 'Memo_' + this.cidstd + '.' + this.pridstd + '.pdf ausdrucken?')) {
      pdfMake.createPdf(this.documentDefinition as any).print();
    }

    await this.safePdf();

    this.alertDispatcher.alertConfig.message = 'Datei ' + 'Memo_' + this.cidstd + '.' + this.pridstd + '.pdf wurde an den Drucker übergeben.';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
  }

  safePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);
    documentGenerator.getBlob((blob: Blob) => {
      // Convert Blob to File
      const file = new File(
        [blob], // Blob content
        `Memo_${this.cidstd}.${this.pridstd}.pdf`, // File name
        { type: blob.type, lastModified: Date.now() } // File metadata
      );

      // Now, send the file instead of the Blob
      this.fss.sendFile(file, 50, 0, this.cidstd + '.' + this.pridstd, 0, '', `Memo ${this.cidstd}.${this.pridstd}`)
        .subscribe({
          next: (data) => console.log("done"),
          error: (error) => console.error("Error:", error),
          complete: () => console.log("Upload completed")
        });
    });
  }

  showModal(fileid: string) {
    const phmToken = getTenantStorage('access_token');
    this.modalUrl = `${this.filesDirectory}/download_file?file_id=${fileid}&token=${phmToken}`;
    this.showPDF = true;
  }

}
